import PropTypes from 'prop-types';
import React from 'react';
import { findDOMNode } from 'react-dom';
import { Element, animateScroll, scroller } from 'react-scroll';
import {
    DEFAULT_STEP,
    DEFAULT_CONTAINER_ID,
    DIRECTION_UP,
    DIRECTION_DOWN,
    SCROLL_BUTTON_HIGHLIGHTED,
    SCROLL_TO_DEFAULT_SECTION,
    SECOND_PART_SEGMENT_INDEX,
    SCROLL_ANIMATION_DURATION,
} from '../../constants/slider';
import { ARIA_LABELS } from '../../constants/text';

export default class TextPaneScroll extends React.Component {
    static getContentElement(content, index) {
        return (
            <Element name={`segment-${index}`} key={index}>
                {content}
            </Element>
        );
    }

    static isButtonHighLighted(event) {
        return event && event.target.classList.contains(SCROLL_BUTTON_HIGHLIGHTED);
    }

    constructor(props) {
        super(props);
        this.setDraggerPosition = this.setDraggerPosition.bind(this);
        this.updateDraggerPosition = this.updateDraggerPosition.bind(this);
        this.initDefaultDraggerStep = this.initDefaultDraggerStep.bind(this);
        this.getCustomScrollBar = this.getCustomScrollBar.bind(this);
        this.scrollToSection = this.scrollToSection.bind(this);
        this.scroll = this.scroll.bind(this);
        this.scrollUp = this.scrollUp.bind(this);
        this.scrollDown = this.scrollDown.bind(this);
        this.scrollToBottom = this.scrollToBottom.bind(this);
        this.setScrollDownTabIndex = this.setScrollDownTabIndex.bind(this);
        this.setScrollUpTabIndex = this.setScrollUpTabIndex.bind(this);
        this.state = {
            isButtonDisabled: false,
        };
    }

    componentDidUpdate(prevProps) {
        if (this.props.isEnabled && !prevProps.isEnabled) {
            this.draggerStep = this.initDefaultDraggerStep();
            this.scrollToSection();
            this.setDraggerPosition();
        }
    }

    setDraggerPosition() {
        let defaultSection = this.segmentPartOne;

        if (this.segmentPartTwo && this.segmentPartTwo.id === this.props.scrollToSection) {
            defaultSection = this.segmentPartTwo;
        }

        const draggerY =
            Math.round(defaultSection.offsetTop / this.props.scrollStep) * this.draggerStep;
        if (this.props.isDraggerVisible) {
            this.draggerBar.style.top = `${draggerY}px`;
        }
    }

    setScrollUpTabIndex() {
        switch (true) {
            case this.props.isScrollModelPage && !this.props.isTabIndexDisabled:
                return '2';
            case this.props.isTabIndexDisabled:
                return '-1';
            default:
                return 0;
        }
    }

    setScrollDownTabIndex() {
        switch (true) {
            case this.props.isTabIndexDisabled:
                return '-1';
            default:
                return 0;
        }
    }

    getCustomScrollBar() {
        return (
            <div>
                <button
                    className="scroll-btn up"
                    aria-label={ARIA_LABELS.SCROLL_UP}
                    data-dd-action-name="scroll-up"
                    onClick={this.scrollUp}
                    tabIndex={this.setScrollUpTabIndex()}
                    disabled={this.state.isButtonDisabled}
                />
                <div
                    className="dragger-wrapper"
                    ref={wrapper => {
                        this.draggerWrapper = wrapper;
                    }}
                >
                    <div className="dragger-bar-container">
                        {this.props.isDraggerVisible && (
                            <div
                                className="dragger-bar"
                                ref={dragger => {
                                    this.draggerBar = dragger;
                                }}
                            />
                        )}
                    </div>
                    <div className="dragger-rail" />
                </div>
                <button
                    tabIndex={this.setScrollDownTabIndex()}
                    aria-label={ARIA_LABELS.SCROLL_DOWN}
                    className={`scroll-btn down
                        ${this.props.isScrollDownButtonHighlighted ? ' pulse' : ''}`}
                    data-dd-action-name="scroll-down"
                    onClick={this.scrollDown}
                    disabled={this.state.isButtonDisabled}
                />
            </div>
        );
    }

    updateDraggerPosition(direction) {
        const scrollBarHeight = this.draggerWrapper.offsetHeight;
        let draggerOffsetY = this.draggerBar.offsetTop;

        if (direction === DIRECTION_UP) {
            draggerOffsetY -= this.draggerStep;
        } else {
            draggerOffsetY += this.draggerStep;
        }

        if (draggerOffsetY >= 0 && draggerOffsetY <= scrollBarHeight) {
            this.draggerBar.style.top = `${draggerOffsetY}px`;
        }
    }

    scrollUp() {
        this.scroll(DIRECTION_UP);
    }

    scrollDown(event) {
        if (TextPaneScroll.isButtonHighLighted(event)) {
            this.props.onScrollDownButtonClick();
        } else {
            this.scroll(DIRECTION_DOWN);
        }
    }

    scrollToBottom(duration = SCROLL_ANIMATION_DURATION) {
        const scrollBarHeight = this.draggerWrapper.clientHeight;
        animateScroll.scrollToBottom({
            containerId: this.props.scrollContainerId,
            duration,
        });
        if (this.props.isDraggerVisible) {
            this.draggerBar.style.top = `${scrollBarHeight}px`;
        }
    }

    scroll(direction) {
        let { scrollStep } = this.props;

        this.setState({
            isButtonDisabled: true,
        });

        if (direction === DIRECTION_UP) {
            scrollStep = -this.props.scrollStep;
        }

        animateScroll.scrollMore(scrollStep, {
            containerId: this.props.scrollContainerId,
            duration: SCROLL_ANIMATION_DURATION,
        });

        if (this.props.isDraggerVisible) {
            this.updateDraggerPosition(direction);
        }

        setTimeout(() => {
            this.setState({
                isButtonDisabled: false,
            });
        }, SCROLL_ANIMATION_DURATION);
    }

    initDefaultDraggerStep() {
        const container = findDOMNode(this.scrollContainer);
        const scrollBarHeight = this.draggerWrapper.clientHeight;
        const scrollHiddenHeight = container.scrollHeight - container.clientHeight;

        return Math.floor(scrollBarHeight / Math.ceil(scrollHiddenHeight / this.props.scrollStep));
    }

    scrollToSection() {
        scroller.scrollTo(this.props.scrollToSection, {
            containerId: this.props.scrollContainerId,
            to: this.props.scrollToSection,
            duration: 0,
            delay: 0,
            smooth: false,
            isDynamic: true,
        });
    }

    render() {
        return (
            <div className="comprehension-text">
                <div className="custom-scroll-wrapper">
                    <div className="custom-scroll-container">
                        <Element
                            id={this.props.scrollContainerId}
                            ref={container => {
                                this.scrollContainer = container;
                            }}
                            style={{ position: 'relative' }}
                        >
                            <div
                                id="segment-part-one"
                                className="segment-wrapper"
                                ref={segmentOne => {
                                    this.segmentPartOne = segmentOne;
                                }}
                            >
                                {this.props.content
                                    .filter((segment, i) => i < SECOND_PART_SEGMENT_INDEX)
                                    .map((segment, i) =>
                                        TextPaneScroll.getContentElement(segment, i),
                                    )}
                            </div>
                            {this.props.content.length > SECOND_PART_SEGMENT_INDEX && (
                                <div
                                    id="segment-part-two"
                                    className="segment-wrapper"
                                    ref={segmentTwo => {
                                        this.segmentPartTwo = segmentTwo;
                                    }}
                                >
                                    {this.props.content
                                        .filter((segment, i) => i >= SECOND_PART_SEGMENT_INDEX)
                                        .map((segment, i) =>
                                            TextPaneScroll.getContentElement(
                                                segment,
                                                i + SECOND_PART_SEGMENT_INDEX,
                                            ),
                                        )}
                                </div>
                            )}
                        </Element>
                    </div>
                    <div className="custom-scroll-bar">{this.getCustomScrollBar()}</div>
                </div>
            </div>
        );
    }
}

TextPaneScroll.propTypes = {
    content: PropTypes.array,
    isEnabled: PropTypes.bool,
    scrollStep: PropTypes.number,
    scrollContainerId: PropTypes.string,
    scrollToSection: PropTypes.string,
    isScrollDownButtonHighlighted: PropTypes.bool,
    isTabIndexDisabled: PropTypes.bool,
    isDraggerVisible: PropTypes.bool,
    isScrollModelPage: PropTypes.bool,
    onScrollDownButtonClick: PropTypes.func,
};

TextPaneScroll.defaultProps = {
    content: [],
    isEnabled: false,
    scrollStep: DEFAULT_STEP,
    scrollContainerId: DEFAULT_CONTAINER_ID,
    scrollToSection: SCROLL_TO_DEFAULT_SECTION,
    isScrollDownButtonHighlighted: false,
    isDraggerVisible: true,
    isTabIndexDisabled: false,
    isScrollModelPage: false,
    onScrollDownButtonClick: () => {},
};
